/**
 * RouteErrorBoundary — catches uncaught throws from the route tree so the
 * user is never left staring at a spinner forever.
 *
 * Most common trigger: a `React.lazy()` chunk fails to load. This happens
 * routinely on Vercel preview rebuilds (the user's browser holds a stale
 * `main-XXX.js` that references chunks whose hashes changed in the new
 * deploy). Without this boundary the Suspense fallback shows forever,
 * which matches Rafael's "pages loading forever" report (notably on
 * Admin pages which are all lazy-loaded — see `routes/index.tsx`).
 *
 * The boundary swallows the error, surfaces a friendly explanation,
 * and offers a hard reload (which fetches fresh asset manifests).
 *
 * It is intentionally lightweight: no Sentry hookup yet, no telemetry.
 * Phase 9 (observability) wires Sentry into componentDidCatch.
 */

import * as React from 'react';

interface State {
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
}

export class RouteErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log so devs can find it in console / Sentry later.
    // Chunk-load failures show up as: `ChunkLoadError: Loading chunk N failed.`
    // or `TypeError: Failed to fetch dynamically imported module`.
    console.error('[RouteErrorBoundary] caught', error, info.componentStack);
  }

  private handleReload = () => {
    // Hard reload — bypasses the SPA shell so the browser refetches the
    // current asset manifest. Necessary when the cause is a stale chunk
    // hash; a soft `navigate(0)` wouldn't refresh `index.html`.
    window.location.reload();
  };

  override render() {
    if (!this.state.error) return this.props.children;

    const isChunkError =
      /chunk|dynamically imported module|importing a module/i.test(
        this.state.error.message ?? '',
      );

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <h2 className="text-lg font-semibold text-text-primary mb-2">
          {isChunkError ? 'A newer version of the app is available' : 'Something went wrong'}
        </h2>
        <p className="text-sm text-text-secondary max-w-md mb-4">
          {isChunkError
            ? 'Reload the page to fetch the latest version. Your work is not affected.'
            : 'The page failed to load. Reloading usually fixes it.'}
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="inline-flex items-center px-4 py-2 rounded-md bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors"
        >
          Reload page
        </button>
        {import.meta.env.DEV && (
          <pre className="mt-6 text-xs text-text-muted max-w-xl overflow-auto bg-surface-elevated p-3 rounded border border-border-subtle text-start">
            {this.state.error.message}
          </pre>
        )}
      </div>
    );
  }
}
